'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useMemo } from 'react';

import { Flex } from '@/components/common/Flex';
import { Select, type SelectProps } from '@/components/common/select/Select';
import { Spinner } from '@/components/common/Spinner';
import { Text } from '@/components/common/Text';
import { isDefined } from '@/utils/utils';

export type BaseItem = {
  id: string;
  name?: unknown;
};

export type BaseAsyncSelectProps<Value extends BaseItem> = Omit<SelectProps<Value>, 'options'>;

export type AsyncSelectProps<Value extends BaseItem> = BaseAsyncSelectProps<Value> & {
  loading: boolean;
  options: Value[] | null | undefined;
  formatName?: (option: NoInfer<Value>) => string;
};

const EMPTY_OPTIONS: never[] = [];

export const AsyncSelect = <Value extends BaseItem>(props: AsyncSelectProps<Value>) => {
  const { value, loading, options: propsOptions, formatName, placeholder, emptyListNode, ...selectProps } = props;

  const options = propsOptions ?? EMPTY_OPTIONS;

  const t = useTranslations('misc.select.any');

  const isOptionsFirstLoading = useMemo(() => !isDefined(propsOptions) && loading, [loading, propsOptions]);

  const getOptionNode = useCallback(
    (option: Value) => {
      return formatName?.(option) ?? (typeof option.name === 'string' ? option.name : option.id);
    },
    [formatName]
  );

  const selectOptions = useMemo(() => {
    return options.map((option) => ({
      node: getOptionNode(option),
      value: option,
    }));
  }, [getOptionNode, options]);

  return (
    <Select<Value>
      value={isOptionsFirstLoading ? undefined : value}
      options={selectOptions}
      placeholder={
        isOptionsFirstLoading ? (
          <Flex gap={2} alignItems="center">
            <Spinner size={20} color="white-50p" />

            <Text color="white-50p">{t('loadingPlaceholder')}</Text>
          </Flex>
        ) : (
          placeholder
        )
      }
      emptyListNode={loading ? <Spinner size={20} color="current" /> : emptyListNode}
      {...selectProps}
    />
  );
};
